import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";

// Routes
import AuthRoute from "./views/routes/authRoute";
import MainRoute from "./views/routes/mainRoute";

// Auth Pages
import Login from "./views/auth/login/page";
import ForgotPassword from "./views/auth/forgot/page";
import ResetPassword from "./views/auth/resetPassword/page";

// Pages
import Dashboard from "./views/main/home/page";

// Configration Setup
import { useDispatch, useSelector } from "react-redux";

// global routes
import NotFound from "./notFound";

import PayrollRoute from "./views/main/payroll/page";
import Alerts from "./views/main/alerts/page";
import Reminder from "./views/main/reminder/page";
import Profile from "./views/main/profile/page";
import { _fetchUserData } from "./store/actions/user";
import useCurrentLocation from "./hooks/useCurrentLocation";
import { getItem } from "./utils/localStorage";
import ProfileView from "./views/main/profile/profileView";
import CommonProfileView from "./views/main/profile/commonProfileView";
import AddReminder from "./views/main/reminder/addReminder";
import useFirebaseMessaging from "./hooks/useFirebaseMessaging";
import DetailPage from "./views/main/helpCenter/innerScreens/detailPage/page";
import HelpCenterRoute from "./views/main/helpCenter/page";
import HelpCenter from "./views/main/helpCenter/innerScreens/mainPage/page";
import ThankYouPage from "./views/main/thankYouPage/page";
import ChangePassword from "./views/main/settings/innerpages/changePassword/page";
import ReportSetup from "./views/main/reports/innerScreens/mainPage/page";
import ReportsSetupRoute from "./views/main/reports/page";
import AcademicReports from "./views/main/reports/innerScreens/academicReports/page";
import AttendanceReports from "./views/main/reports/innerScreens/attendanceReports/page";
import TeacherPerformanceReports from "./views/main/reports/innerScreens/teacherPerformReports/page";
import ExaminationReports from "./views/main/reports/innerScreens/examinationReport/page";
import QuestionDetailPage from "./views/main/helpCenter/innerScreens/questionDetailPage/page";
import OnboardingRoute from "./views/routes/onboardingRoute";
import renderBasicRoutes from "./views/routes/modulesRoute/basic.route";
import renderConfigurationRoutes from "./views/routes/modulesRoute/configuration.route";
import renderInventoryRoutes from "./views/routes/modulesRoute/inventory.route";
import renderPayrollRoutes from "./views/routes/modulesRoute/payroll.route";
import EditProfile from "./views/main/profile/editProfile";
import Calendar from "./views/main/calander/page";
import CommonRoute from "./views/routes/commonRoute";
import renderHrRoutes from "./views/routes/modulesRoute/hr.route";
import renderLeadsRoutes from "./views/routes/modulesRoute/leads.route";
import renderPhotosRoutes from "./views/routes/modulesRoute/photos.route";
import renderLeavesRoutes from "./views/routes/modulesRoute/leaves.route";
import { ExportPanel } from "./components/export";
import renderRecruitmentRoutes from "./views/routes/modulesRoute/recuritment.route";
import renderReimbursementRoutes from "./views/routes/modulesRoute/reimbursement.route";
import renderReportsRoutes from "./views/routes/modulesRoute/reports.route";
import renderStaffRoutes from "./views/routes/modulesRoute/staff.route";
import JobApplicationForm from "./views/main/jobApplicationForm/page";
import StaffRoute from "./views/routes/staffRoute";

const App = () => {
  const {} = useCurrentLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state) => state.user);
  const { permission, requestForToken } = useFirebaseMessaging();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      const disableContextMenu = (e) => {
        e.preventDefault();
      };

      document.addEventListener("contextmenu", disableContextMenu);
      return () => {
        document.removeEventListener("contextmenu", disableContextMenu);
      };
    }
  }, []);

  // Fetch fresh profile data on mount AND whenever isAuthenticated changes true.
  // This covers both:
  //   - Page reload: token exists at mount → fetch immediately
  //   - First login: isAuthenticated flips false→true → fetch so menu data is up-to-date
  useEffect(() => {
    const token = getItem("user")?.token;
    if (token && isAuthenticated) {
      dispatch(_fetchUserData());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (permission === "granted" && isAuthenticated) {
      requestForToken();
    }
  }, [permission, requestForToken, isAuthenticated]);

  return (
    <BrowserRouter>
      <Routes>
        {/*Auth*/}
        <Route path="/auth" element={<AuthRoute />}>
          <Route path={"/auth/login"} element={<Login />} />
          <Route path={"/auth/forgot"} element={<ForgotPassword />} />
          <Route path={"/auth/reset-password"} element={<ResetPassword />} />
        </Route>

        {/* Onboarding  */}
        <Route path="/onboarding" element={<OnboardingRoute />}></Route>

        {/*Main*/}
        <Route path="/" element={<MainRoute />}>
          {/*Dashboard*/}
          <Route path={"/"} element={<Dashboard />} />

          {/*Basic Setup*/}
          <Route path={"/configuration"} element={<CommonRoute />}>
            {renderBasicRoutes()}
          </Route>

          {/*Basic Setup*/}
          <Route path={"/hr"} element={<CommonRoute />}>
            {renderHrRoutes()}
          </Route>

          {/*Leaves*/}
          <Route path={"/leaves"} element={<CommonRoute />}>
            {renderLeavesRoutes()}
          </Route>

          {/*Leads*/}
          <Route path={"/leads"} element={<CommonRoute />}>
            {renderLeadsRoutes()}
          </Route>


          <Route path="/recruitment" element={<CommonRoute />}>
            {renderRecruitmentRoutes()}
          </Route>

          {/*Reimbursement*/}
          <Route path="/reimbursement" element={<CommonRoute />}>
            {renderReimbursementRoutes()}
          </Route>

          {/*Reports & Analytics*/}
          <Route path="/reports" element={<CommonRoute />}>
            {renderReportsRoutes()}
          </Route>

          <Route path={"/photos"} element={<CommonRoute />}>
            {renderPhotosRoutes()}
          </Route>

          {/*profile*/}
          <Route path={"/profile"} element={<Profile />}>
            <Route index element={<ProfileView />} />
            <Route path="edit" element={<EditProfile />} />
          </Route>

          {/*profile view*/}
          <Route path={"/profile/view/:id"} element={<Profile />}>
            <Route index element={<CommonProfileView />} />
          </Route>

          {/*help center*/}
          <Route path={"/help"} element={<HelpCenterRoute />}>
            <Route path={"/help"} element={<HelpCenter />} />
            <Route path={"detail/:id"} element={<DetailPage />} />
            <Route
              path={"question/detail/:id"}
              element={<QuestionDetailPage />}
            />
          </Route>

          {/*Configuration*/}
          <Route path={"/configuration"} element={<CommonRoute />}>
            {renderConfigurationRoutes()}
          </Route>

          {/*School Reports (Old Module - Moved to avoid conflict with Reports & Analytics)*/}
          <Route path={"/school-reports"} element={<ReportsSetupRoute />}>
            <Route path={"/school-reports"} element={<ReportSetup />} />
            <Route path={"/school-reports/academic"} element={<AcademicReports />} />
            <Route
              path={"/school-reports/attendance/student"}
              element={<AttendanceReports />}
            />
            <Route
              path={"/school-reports/attendance/staff"}
              element={<AttendanceReports />}
            />
            <Route
              path={"/school-reports/teacher-performance"}
              element={<TeacherPerformanceReports />}
            />
            <Route
              path={"/school-reports/examination"}
              element={<ExaminationReports />}
            />
          </Route>

          {/*Inventory*/}
          <Route path={"/inventory"} element={<CommonRoute />}>
            {renderInventoryRoutes()}
          </Route>

          {/* Payroll */}
          <Route path={"/payroll"} element={<PayrollRoute />}>
            {renderPayrollRoutes()}
          </Route>

          {/*Staff*/}
          <Route path={"/staff"} element={<StaffRoute />}>
            {renderStaffRoutes()}
          </Route>

          {/*Alert*/}
          <Route>
            <Route path={"/alert"} element={<Alerts />} />
            <Route path={"/reminder"} element={<Reminder />} />
            <Route path={"/calendar"} element={<Calendar />} />
            <Route path={"/reminder/add"} element={<AddReminder />} />
          </Route>

          <Route path="/change-password" element={<ChangePassword />} />
        </Route>

        {/* Other routes */}
        <Route path="*" element={<NotFound />} />
        <Route path="/job-application/:id?" element={<JobApplicationForm />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
      </Routes>

      {/* Export Panel - appears globally when exports are active */}
      <ExportPanel />
    </BrowserRouter>
  );
};

export default App;
